/** Executive-language helpers — translates technical API values into
 *  business-friendly labels for the CEO/leadership audience. */

export function execStanceLabel(stance?: string | null): string {
  switch ((stance ?? '').trim().toLowerCase()) {
    case 'bullish':
    case 'positive':
      return 'Positive Momentum';
    case 'bearish':
    case 'negative':
      return 'Competitive Pressure';
    case 'cautious':
      return 'Growth Watch';
    case 'neutral':
      return 'Stable Outlook';
    case 'mixed':
      return 'Mixed Signals';
    default:
      return 'Outlook Pending';
  }
}

export function execConfidenceLabel(confidence?: string | null): string {
  switch ((confidence ?? '').trim().toLowerCase()) {
    case 'high':
      return 'High Conviction';
    case 'medium':
      return 'Moderate Confidence';
    case 'low':
      return 'Limited Visibility';
    default:
      return '—';
  }
}

export function urgencyLabel(severity?: string | null): string {
  switch ((severity ?? '').trim().toLowerCase()) {
    case 'critical':
      return 'Urgent';
    case 'high':
      return 'High Priority';
    case 'medium':
      return 'Monitor';
    case 'low':
      return 'Watch';
    default:
      return 'Review';
  }
}

const BULLISH_RE = /\b(growth|grow|expand|expan|surge|gain|beat|exceed|record|launch|win|opportunit|improv|increas|rise|rose|strong|positive|upside|accelerat|outperform|innovat)\b/i;
const BEARISH_RE = /\b(declin|fall|fell|drop|concern|risk|pressure|headwind|challeng|miss|loss|cut|reduc|warn|negative|weak|delay|slowdown|contraction|downside|struggle|disappoint|erode|shrink)\b/i;

/** Infers bullish / bearish / neutral signal from insight text. */
export function insightSentiment(label: string, implication?: string): 'bullish' | 'bearish' | 'neutral' {
  const text = `${label} ${implication ?? ''}`;
  const bull = (text.match(BULLISH_RE) ?? []).length;
  const bear = (text.match(BEARISH_RE) ?? []).length;
  if (bull > bear) return 'bullish';
  if (bear > bull) return 'bearish';
  return 'neutral';
}

/** Infers a business impact area from free text (insight labels + implications). */
export function businessAreaLabel(text: string): string {
  const t = text.toLowerCase();
  if (/revenue|earning|profit|sales|turnover/.test(t)) return 'Revenue';
  if (/market.share|competit|rival|peer/.test(t)) return 'Market Position';
  if (/growth|expan|demand|volume|scale/.test(t)) return 'Growth';
  if (/investor|stock|fund|ipo|valuation|capital/.test(t)) return 'Investor Relations';
  if (/supply|operat|manufactur|produc|logis|distribut/.test(t)) return 'Operations';
  if (/regulat|complian|legal|policy|tax|govern/.test(t)) return 'Regulatory';
  if (/brand|customer|consumer|retail|loyalt/.test(t)) return 'Brand & Consumer';
  if (/margin|cost|pric|inflat|tariff/.test(t)) return 'Margins & Costs';
  if (/tech|digital|innovat|ai|data|platform/.test(t)) return 'Technology';
  if (/merger|acqui|partner|joint/.test(t)) return 'M&A & Partnerships';
  return 'Strategic';
}

export const BUSINESS_AREA_COLORS: Record<string, string> = {
  'Revenue': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Growth': 'bg-blue-50 text-blue-700 border-blue-200',
  'Market Position': 'bg-violet-50 text-violet-700 border-violet-200',
  'Investor Relations': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Operations': 'bg-orange-50 text-orange-700 border-orange-200',
  'Regulatory': 'bg-amber-50 text-amber-700 border-amber-200',
  'Brand & Consumer': 'bg-pink-50 text-pink-700 border-pink-200',
  'Margins & Costs': 'bg-red-50 text-red-700 border-red-200',
  'Technology': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'M&A & Partnerships': 'bg-purple-50 text-purple-700 border-purple-200',
  'Strategic': 'bg-neutral-100 text-neutral-600 border-neutral-200',
};
